import { useEffect, useMemo } from 'react';

import { useMarketingCardGroupsQuery } from 'generated/sanity-graphql';

// A global variable is used to preserve the last queried IDs across re-renders.
// This prevents unnecessary refetching when the same IDs are provided in subsequent renders.
// Since React's state and memoized values reset when the component unmounts,
// keeping this reference outside ensures that the logic remains consistent
// across multiple hook calls within the same session.
// TODO: try to avoid using this global variable, and use a ref in a context instead
let lastMarketingCardGroupsIds: string = '';

export const useMarketingCardGroups = (ids?: string[]) => {
  const marketingCardGroupIds = useMemo(() => ids ?? [], [ids]);

  const { data, loading, refetch } = useMarketingCardGroupsQuery({
    variables: {
      where: {
        _id_in: marketingCardGroupIds,
      },
    },
    skip: !marketingCardGroupIds?.length,
  });

  useEffect(() => {
    // Apollo's cache prevents re-executing the query even if the IDs change.
    // To ensure fresh data is fetched when the ID set changes, we compare
    // the current IDs with the last queried ones. If they differ, we trigger a refetch.
    if (marketingCardGroupIds.length > 0) {
      const stringifiedIds = JSON.stringify(marketingCardGroupIds);
      if (stringifiedIds !== lastMarketingCardGroupsIds) {
        lastMarketingCardGroupsIds = stringifiedIds;
        refetch();
      }
    }
  }, [marketingCardGroupIds, refetch]);

  const marketingCardGroups = data?.allMarketingCardGroups;

  return {
    marketingCardGroups,
    marketingCardGroupsLoading: loading,
  };
};
